import { useTranslation } from 'react-i18next'
import { Navigate, useParams } from 'react-router'

import { ArrowRightIcon, ArticleIcon } from 'app/common/presentation/icons'
import { useObjectives } from 'app/objectives/application'
import { ObjectiveCalculation } from 'app/objectives/presentation/components'
import { ObjectiveFilter } from 'app/objectives/types'

import { PageBanner } from 'components'

import styles from './ObjectiveDetails.module.scss'

const formatDate = (value: string) => {
  const parts = new Intl.DateTimeFormat('en', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).formatToParts(new Date(value))
  const getPart = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? ''

  return `${getPart('day')} ${getPart('month')} ${getPart('year')}`
}

const getFilterParts = (filter: ObjectiveFilter): (string | number)[] => {
  switch (filter.type) {
    case 'event':
    case 'user':
      return [
        filter.type === 'user'
          ? 'user trait'
          : filter.operator === 'withinLastDays'
            ? 'timebased'
            : 'event trait',
        filter.property,
        filter.operator,
        ...(filter.value === undefined ? [] : [filter.value]),
      ]
    case 'eventCount':
      return ['eventCount', filter.event, filter.occurrences.operator, filter.occurrences.value]
    case 'userHistory':
      return [
        'user history',
        filter.event,
        ...filter.conditions.flatMap(({ operator, property, value }) => [
          property,
          operator,
          value,
        ]),
        filter.occurrences.operator,
        filter.occurrences.value,
      ]
    case 'sequence':
      return [
        'sequence',
        ...filter.steps.flatMap(({ event, negate, sequenceNumber }) => [
          sequenceNumber,
          ...(negate ? ['not'] : []),
          event,
        ]),
      ]
  }
}

const ObjectiveDetails = () => {
  const { id } = useParams()
  const { t } = useTranslation('objectives')
  const { getObjective } = useObjectives()
  const objective = id ? getObjective(id) : null

  if (!id) {
    return <Navigate replace to="/objectives" />
  }

  if (!objective) {
    return null
  }

  return (
    <div className={styles.objectiveDetails}>
      <PageBanner title={objective.name}>
        <span className={styles.createdAt}>
          {t('details.created')} <strong>{formatDate(objective.createdAt)}</strong>
        </span>
      </PageBanner>

      <div className={styles.cards}>
        <section className={styles.card}>
          <header className={styles.cardHeader}>
            <ArticleIcon />
            <h2>{t('details.definition')}</h2>
          </header>

          <div className={styles.cardContent}>
            <div className={styles.formula}>
              <ObjectiveCalculation
                aggregation={objective.aggregation}
                events={objective.events}
                per={objective.per}
              />
              <ArrowRightIcon className={styles.directionArrow} />
              <span>{objective.direction}</span>
            </div>

            {objective.filters.length > 0 && (
              <div className={styles.filters}>
                <h3>{t('details.filters')}</h3>
                <div className={styles.filterList}>
                  {objective.filters.map((filter, filterIndex) => {
                    const [label, ...parts] = getFilterParts(filter)

                    return (
                      <div className={styles.filterRow} key={filterIndex}>
                        <span className={styles.filterLabel}>{label}</span>
                        {parts.map((part, partIndex) => (
                          <code key={`${String(part)}-${partIndex}`}>{part}</code>
                        ))}
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  )
}

export default ObjectiveDetails
